import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Spin } from "antd";
import {
  CloseOutlined,
  PauseOutlined,
  CaretRightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { getVideoUrl, getWorkflowVideoUrl } from "../../utils/videoStorage";

// Custom icons for replay/forward 10 seconds
const Replay10Icon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8zm-1.31 8.9l.25-2.17h2.39v.71h-1.7l-.11.92c.03-.02.07-.03.11-.05s.09-.04.15-.05.12-.03.18-.04.13-.02.2-.02c.21 0 .39.03.55.1s.3.16.41.28.2.27.25.45.09.38.09.6c0 .19-.03.37-.09.54s-.15.32-.27.45-.27.24-.45.31-.39.12-.64.12c-.18 0-.36-.03-.53-.08s-.32-.14-.46-.24-.24-.24-.32-.39-.13-.33-.13-.53h.84c.02.18.08.32.19.41s.25.15.42.15c.11 0 .2-.02.27-.06s.14-.1.18-.17.08-.15.11-.25.03-.2.03-.31-.01-.21-.04-.31-.07-.17-.13-.24-.13-.12-.21-.15-.19-.05-.3-.05c-.08 0-.15.01-.2.02s-.11.03-.15.05-.08.05-.12.07-.07.06-.1.09l-.67-.16zm-1.86 1.68v-5h1.18v5H8.83z"
      fill="white"
    />
  </svg>
);

const Forward10Icon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 13c0 3.31-2.69 6-6 6s-6-2.69-6-6 2.69-6 6-6v4l5-5-5-5v4c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8h-2zm-7.46 2.22c-.06.05-.12.09-.2.12s-.17.04-.27.04c-.09 0-.17-.01-.25-.04s-.14-.06-.2-.11-.1-.1-.13-.17-.05-.14-.05-.22h-.85c0 .21.04.39.12.55s.19.28.33.38.29.18.46.23.35.07.53.07c.21 0 .41-.03.6-.08s.34-.14.48-.24.24-.24.32-.39.12-.33.12-.53c0-.23-.06-.44-.18-.61s-.3-.3-.54-.39c.1-.05.2-.1.28-.17s.15-.14.2-.22.1-.16.13-.25.04-.18.04-.27c0-.2-.04-.37-.11-.53s-.17-.28-.3-.38-.28-.18-.46-.24-.37-.08-.59-.08c-.19 0-.38.03-.54.08s-.32.13-.44.23-.23.23-.3.38-.11.33-.11.53h.84c0-.07.02-.14.05-.2s.07-.11.12-.15.11-.07.18-.1.14-.03.22-.03c.1 0 .18.01.25.04s.13.06.18.11.08.11.11.17.04.14.04.22c0 .18-.05.32-.16.43s-.26.16-.48.16h-.43v.66h.45c.11 0 .2.01.29.04s.16.06.22.11.11.12.14.2.05.18.05.29c0 .09-.01.17-.04.24s-.08.13-.14.18zm3.9.01c-.12.17-.27.3-.44.39s-.37.14-.59.14-.43-.05-.6-.14-.32-.22-.44-.39-.22-.37-.28-.6-.1-.5-.1-.8.03-.56.1-.8.16-.43.28-.6.27-.3.44-.39.38-.14.6-.14.42.05.59.14.33.22.44.39.22.37.29.6.1.5.1.8-.03.56-.1.8-.16.43-.29.6zm-.52-2.4c-.06-.14-.14-.25-.25-.33s-.23-.12-.38-.12-.28.04-.38.12-.18.2-.24.33-.1.29-.12.46-.03.35-.03.54.01.36.03.54.06.33.12.46.14.24.24.33.23.12.38.12.28-.04.38-.12.19-.19.25-.33.1-.29.12-.46.03-.35.03-.54-.01-.36-.03-.54-.06-.33-.12-.46z"
      fill="white"
    />
  </svg>
);

const VolumeIcon = ({ muted }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {muted ? (
      <path
        d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
        fill="white"
      />
    ) : (
      <path
        d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"
        fill="white"
      />
    )}
  </svg>
);

const VideoPlayerModal = ({ open, onClose, video }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [buffered, setBuffered] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Resolve the video src from Firebase Storage. The `videoKind` discriminator
  // selects the bucket prefix: workflow demos live under
  // `dashai_workflow_videos/`, dashboard tutorials under `dashone_tutorial_videos/`.
  useEffect(() => {
    if (!open) return;
    if (!video?.storagePath) return;

    setIsLoading(true);
    setError(null);
    setVideoUrl(null);

    const resolver = video.videoKind === "workflow" ? getWorkflowVideoUrl : getVideoUrl;
    resolver(video.storagePath)
      .then((url) => {
        setVideoUrl(url);
      })
      .catch((err) => {
        console.error("Failed to load video:", err);
        setError("Failed to load video. Please try again later.");
        setIsLoading(false);
      });
  }, [open, video?.storagePath, video?.videoKind]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
      setDuration(0);
      setIsLoading(true);
      setVideoUrl(null);
      setError(null);
      setBuffered(0);
    }
  }, [open]);

  // Handle controls visibility
  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleReplay10 = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(
        0,
        videoRef.current.currentTime - 10
      );
    }
  };

  const handleForward10 = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        videoRef.current.currentTime + 10
      );
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      setCurrentTime(current);
      setDuration(total);
      setProgress((current / total) * 100);
      
      // Update buffered amount
      if (videoRef.current.buffered.length > 0) {
        const bufferedEnd = videoRef.current.buffered.end(
          videoRef.current.buffered.length - 1
        );
        setBuffered((bufferedEnd / total) * 100);
      }
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current && videoRef.current.duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const width = rect.width;
      const newTime = (clickX / width) * videoRef.current.duration;
      videoRef.current.currentTime = newTime;
    }
  };

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoLoaded = () => {
    setIsLoading(false);
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVideoError = () => {
    setError("Failed to play video. Please check your connection and try again.");
    setIsLoading(false);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault();
          handlePlayPause();
          break;
        case "ArrowLeft":
          e.preventDefault();
          handleReplay10();
          break;
        case "ArrowRight":
          e.preventDefault();
          handleForward10();
          break;
        case "m":
          e.preventDefault();
          handleMuteToggle();
          break;
        case "f":
          e.preventDefault();
          handleFullscreen();
          break;
        case "Escape":
          if (document.fullscreenElement) {
            document.exitFullscreen();
            setIsFullscreen(false);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // Handlers are recreated each render; the effect keys on the state they read
    // (open/isPlaying/isMuted). Adding them would re-bind the listener every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isPlaying, isMuted]);

  if (!video) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={900}
      destroyOnClose
      styles={{
        content: {
          padding: 0,
          borderRadius: "24px",
          overflow: "hidden",
        },
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.45)",
        },
      }}
    >
      <div
        className="bg-white flex flex-col gap-4 p-6 rounded-[24px]"
        style={{
          boxShadow: "0px 4px 40px 0px rgba(69, 71, 69, 0.2)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-1">
          <div className="flex flex-col gap-1">
            <p
              className="text-base font-medium leading-5 tracking-[-0.16px] text-[#27272a]"
              style={{ fontFamily: "Satoshi, sans-serif" }}
            >
              {video.category}
            </p>
            <p
              className="text-xl font-semibold leading-7 text-[#27272a]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {video.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <CloseOutlined style={{ fontSize: "20px", color: "#27272a" }} />
          </button>
        </div>

        {/* Video Player Area */}
        <div
          ref={containerRef}
          className="bg-black flex flex-col items-center justify-end rounded-[24px] overflow-hidden relative cursor-pointer"
          style={{ minHeight: "400px", aspectRatio: "16/9" }}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => isPlaying && setShowControls(false)}
          onClick={(e) => {
            // Only toggle play if clicking on video area, not controls
            if (e.target === e.currentTarget || e.target.tagName === "VIDEO") {
              handlePlayPause();
            }
          }}
        >
          {/* Video Element */}
          <div className="absolute inset-0 flex items-center justify-center">
            {error ? (
              <div className="text-white text-lg text-center px-8">
                <p className="text-red-400 mb-2">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    setIsLoading(true);
                    if (video?.storagePath) {
                      const resolver = video.videoKind === "workflow" ? getWorkflowVideoUrl : getVideoUrl;
                      resolver(video.storagePath)
                        .then((url) => setVideoUrl(url))
                        .catch(() => setError("Failed to load video."));
                    }
                  }}
                  className="text-[#0E49B5] underline"
                >
                  Try again
                </button>
              </div>
            ) : videoUrl ? (
              <video
                ref={videoRef}
                src={videoUrl}
                className="w-full h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleVideoLoaded}
                onCanPlay={handleVideoLoaded}
                onWaiting={() => setIsLoading(true)}
                onPlaying={() => setIsLoading(false)}
                onEnded={() => {
                  setIsPlaying(false);
                  setShowControls(true);
                }}
                onError={handleVideoError}
                playsInline
                preload="metadata"
              />
            ) : (
              <div className="text-white text-lg opacity-50">
                Loading video...
              </div>
            )}
            
            {/* Loading Spinner */}
            {isLoading && videoUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <Spin
                  indicator={
                    <LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />
                  }
                />
              </div>
            )}

            {/* Play button overlay when paused */}
            {!isPlaying && !isLoading && videoUrl && !error && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
              >
                <div className="w-20 h-20 rounded-full bg-[#0E49B5]/90 flex items-center justify-center hover:bg-[#0E49B5] transition-colors hover:scale-110">
                  <CaretRightOutlined style={{ fontSize: "40px", color: "white", marginLeft: "4px" }} />
                </div>
              </div>
            )}
          </div>

          {/* Controls Overlay */}
          <div
            className={`absolute bottom-0 left-0 right-0 flex flex-col gap-3 px-6 py-4 z-10 bg-gradient-to-t from-black/70 via-black/40 to-transparent transition-opacity duration-300 ${
              showControls || !isPlaying ? "opacity-100" : "opacity-0"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Progress Bar */}
            <div
              className="h-5 w-full cursor-pointer flex items-center group"
              onClick={handleProgressClick}
            >
              <div className="relative w-full h-1 group-hover:h-2 transition-all bg-[rgba(255,255,255,0.3)] rounded-full">
                {/* Buffered portion */}
                <div
                  className="absolute left-0 top-0 h-full bg-[rgba(255,255,255,0.4)] rounded-full"
                  style={{ width: `${buffered}%` }}
                />
                {/* Played portion */}
                <div
                  className="absolute left-0 top-0 h-full bg-[#0E49B5] rounded-full"
                  style={{ width: `${progress}%` }}
                />
                {/* Progress handle */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-[#0E49B5] rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ left: `calc(${progress}% - 8px)` }}
                />
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between w-full">
              {/* Left Controls */}
              <div className="flex gap-3 items-center">
                <button
                  onClick={handlePlayPause}
                  className="p-1 hover:opacity-80 transition-opacity"
                  title={isPlaying ? "Pause (k)" : "Play (k)"}
                >
                  {isPlaying ? (
                    <PauseOutlined style={{ fontSize: "24px", color: "white" }} />
                  ) : (
                    <CaretRightOutlined
                      style={{ fontSize: "24px", color: "white" }}
                    />
                  )}
                </button>
                <button
                  onClick={handleReplay10}
                  className="p-1 hover:opacity-80 transition-opacity"
                  title="Rewind 10s (←)"
                >
                  <Replay10Icon />
                </button>
                <button
                  onClick={handleForward10}
                  className="p-1 hover:opacity-80 transition-opacity"
                  title="Forward 10s (→)"
                >
                  <Forward10Icon />
                </button>
                <button
                  onClick={handleMuteToggle}
                  className="p-1 hover:opacity-80 transition-opacity"
                  title={isMuted ? "Unmute (m)" : "Mute (m)"}
                >
                  <VolumeIcon muted={isMuted} />
                </button>
                <span
                  className="text-white text-sm ml-2"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>

              {/* Right Controls */}
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleFullscreen}
                  className="p-1 hover:opacity-80 transition-opacity"
                  title="Fullscreen (f)"
                >
                  {isFullscreen ? (
                    <FullscreenExitOutlined
                      style={{ fontSize: "20px", color: "white" }}
                    />
                  ) : (
                    <FullscreenOutlined
                      style={{ fontSize: "20px", color: "white" }}
                    />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <p
          className="text-base font-normal leading-[25px] text-[rgba(0,0,0,0.75)]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {video.description ||
            `An introduction to ${video.title}. This video provides a comprehensive overview of the topic and explains key concepts in detail.`}
        </p>
      </div>
    </Modal>
  );
};

export default VideoPlayerModal;
